import { isArray, merge } from 'lodash';

/**
 * Ensures a value is an array. If frozen, returns a copy.
 * @param {*} value - Value to convert to array
 * @returns {Array} - Array version of the value
 */
export const ensureArray = (value) => {
  if (!isArray(value)) {
    return value ? [value] : [];
  }
  return [...value]; // Clone in case frozen
};

/**
 * Transforms an array of refs using a transform function.
 * @param {Array} arr - Array of refs to transform
 * @param {string} platform - Platform identifier
 * @param {string} prvUuid - Provider UUID
 * @param {Function} transformFn - Function to apply to each ref
 * @returns {Array} - Transformed array
 */
export const transformRefArray = (arr, platform, prvUuid, transformFn) =>
  arr?.map((ref) => transformFn(ref, platform, prvUuid)) || [];

/**
 * Normalizes height value to a number.
 * Handles strings like '500px' and returns the numeric part.
 * @param {string|number} value - Height value
 * @returns {number} - Normalized height
 */
export const normalizeHeight = (value) => {
  if (typeof value === 'string') {
    const match = value.match(/^(\d+)/);
    if (match && match[1]) {
      return Number(match[1]);
    }
    return 500; // default height
  } else if (typeof value === 'number') {
    return value;
  }
  return 500;
};

/**
 * Gets a cookie value by key.
 * @param {string} key - Cookie name
 * @returns {string} - Cookie value or empty string
 */
export const getCookie = (key) => {
  const match = document.cookie.match('(^|;)\\s*' + key + '\\s*=\\s*([^;]+)');
  return match ? match.pop() : '';
};

/**
 * Checks whether a TLD string is a recognized country code (ccTLD)
 * by testing it against the browser's Intl.DisplayNames region database.
 * @param {string} tld - The TLD to check (with or without leading dot, e.g. 'de' or '.de')
 * @returns {boolean} - true if the TLD is a known country code
 */
export const isCountryCodeTLD = (tld) => {
  const code = tld.replace(/^\./, '').toUpperCase();
  if (code.length !== 2) return false;
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    const name = displayNames.of(code);
    return name !== code; // unrecognized codes return themselves
  } catch {
    return false;
  }
};

/**
 * Best-effort function to retrieve the TLD of the user's visible URL.
 * Checks current window URL first, then attempts parent (same-origin)
 * or document.referrer (cross-origin fallback).
 * @returns {string|null} - The TLD (e.g., 'com', 'de'), or null on failure
 */
export const getVisibleTld = () => {
  const extractTld = (url) => {
    if (!url) return null;
    try {
      const hostname = new URL(url).hostname;
      const parts = hostname.split('.');
      return parts[parts.length - 1] || null;
    } catch {
      return null;
    }
  };

  // Not in iframe - return current URL's TLD
  if (window === window.parent) {
    return extractTld(window.location.href);
  }

  // In iframe - try same-origin access to parent
  try {
    const parentUrl = window.parent.location.href;
    return extractTld(parentUrl);
  } catch {
    // Cross-origin - blocked by Same-Origin Policy
  }

  // Fallback to document.referrer
  const referrerUrl = document.referrer;
  if (referrerUrl) {
    return extractTld(referrerUrl);
  }

  // Final fallback - return iframe's own TLD
  return extractTld(window.location.href);
};

/**
 * Orders fields by their `index` property, matching the behavior of
 * `sortFields` in ConfigHandler. Returns a new object.
 * @param {object} fields - Field map keyed by field name.
 * @returns {object} - New object with keys in index order.
 */
function sortFieldsByIndex(fields) {
  if (!fields) return fields;
  const entries = Object.entries(fields);
  entries.sort(([, a], [, b]) => {
    const ai = a?.index ?? Number.POSITIVE_INFINITY;
    const bi = b?.index ?? Number.POSITIVE_INFINITY;
    return ai - bi;
  });
  const result = {};
  for (const [key, value] of entries) {
    result[key] = value;
  }
  return result;
}

/**
 * VariableForm builds its yup resolver at render time from each field
 * definition. If a field carries a `validation` property, the resolver
 * calls `.required()` on it — which explodes if `validation` is a plain JSON
 * object rather than a yup schema. Fields saved via the admin editor are
 * serialized to S3 without yup schemas, but a legacy or hand-edited S3 entry
 * could smuggle a stale `validation` object in. Stripping it here is defence
 * in depth: channel default fields still provide validators via defaults.jsx,
 * and per-product fields rely on `type`/`isRequired` to produce a resolver.
 */
function stripFieldValidation(fields) {
  if (!fields) return fields;
  const result = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value && typeof value === 'object') {
      // eslint-disable-next-line no-unused-vars
      const { validation, ...rest } = value;
      result[key] = rest;
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Drops any keys whose value is exactly `null` from a fields object.
 * Disabled standard fields are emitted as `null` by the admin editor
 * (`appConfigDefaults.js`, around line 357 in the React repo) — JSON cannot
 * serialize `undefined`, so `null` is the over-the-wire tombstone marker.
 * This helper honors that contract at merge time, mirroring the shape and
 * defensive style of `removeUndefinedFields` in `ConfigHandler.jsx`.
 *
 * @param {object|null|undefined} fields - Field map possibly containing nulls.
 * @returns {object} - New object without null-valued keys, or `{}` for nullish input.
 */
export const removeNullFields = (fields) => {
  if (!fields) return {};
  const result = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value !== null) {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Cleans a raw product-scoped appConfig override fetched from the public
 * endpoint into a shape ready to be merged on top of the channel appConfig.
 *
 * Steps:
 *  1. Whitelist top-level keys — only `fields` and `localization` flow
 *     through. Anything else is silently discarded (defense in depth against
 *     stale S3 payloads or regressions in the editor's hidden-sections list).
 *     Note: `callbacks` and `postMessageTarget` are *deliberately excluded*
 *     for now. `addPostMessageCallbacks` in ConfigHandler wraps callbacks
 *     into closures over the channel's postMessageTarget at init time, so
 *     swapping either at product scope wouldn't take effect without a
 *     callback rebind step. That rework is a follow-up — the editor may
 *     save those fields, but the widget ignores them for this round.
 *  2. Apply paywall gating, matching the rules in `ConfigHandler.mergeConfigs`
 *     so an override cannot smuggle in features the account hasn't paid for.
 *     The set of gating flags tracked here must stay in sync with the ones
 *     `mergeConfigs` applies to channel config (except `isThemingAllowed`,
 *     which has no surface in the override whitelist).
 *  3. Sort override fields by their `index` for consistency with channel
 *     fields. Strip each field's `validation` property so a stale S3 payload
 *     can't break `VariableForm.generateValidation`.
 *
 * Returns `null` when no keys survive — callers treat null as "no override".
 *
 * @param {object|null|undefined} raw - The raw JSON fetched from S3.
 * @param {object} gating - Paywall gating flags from Redux.
 * @param {boolean} gating.isLocalisationAllowed
 * @param {boolean} gating.areCustomFieldsAllowed
 * @returns {object|null}
 */
export const processProductOverride = (raw, gating = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  const { isLocalisationAllowed, areCustomFieldsAllowed } = gating;
  const out = {};

  if (raw.fields && typeof raw.fields === 'object' && areCustomFieldsAllowed) {
    out.fields = stripFieldValidation(sortFieldsByIndex(raw.fields));
  }

  if (
    raw.localization &&
    typeof raw.localization === 'object' &&
    isLocalisationAllowed
  ) {
    out.localization = raw.localization;
  }

  if (Object.keys(out).length === 0) return null;

  return out;
};

/**
 * Layers a processed product override on top of an effective appConfig.
 * Non-mutating: returns a new top-level object. Used by `selectAppConfig`
 * after the base effective config is built.
 *
 * Directionality: override wins over base. This layers *on top of* the
 * already-merged channel config, whereas `mergeConfigs` in ConfigHandler
 * merges defaults → given-props for the channel baseline.
 *
 * Fields and localization are deliberately treated asymmetrically:
 *
 *  - **Fields**: when the override carries `fields`, the result is built
 *    from `{ ...defaults.fields, ...override.fields }` and `base.fields` is
 *    intentionally ignored. Channel/in-JS fields don't compose with
 *    product-level fields — the user's mental model is "product config is
 *    a sibling of channel config; both layered over defaults; product wins
 *    for the keys it owns." Nulls in the override act as tombstones and are
 *    stripped via `removeNullFields`, so a disabled standard field
 *    disappears from the merged result.
 *  - **Localization**: override is deep-merged on top of the channel-merged
 *    `base.localization`. Translations are naturally additive — a product
 *    setting `de.foo = 'X'` should not nuke provider/account `de.bar`. The
 *    sparse-storage model relies on this: the editor saves only the user's
 *    edits, and the runtime fills the rest from upstream.
 *
 * The asymmetry is intentional: fields are typically defined holistically
 * (a customer either has a custom field set or doesn't), so mixing in-JS
 * channel fields with product fields creates surprise. Localization layers
 * naturally. Treating them differently honors the user's mental model for
 * each. See the plan doc for full rationale.
 *
 * @param {object} base - The effective appConfig before product override.
 * @param {object|null} override - Output of processProductOverride.
 * @param {object} [defaults] - The bundled appConfig defaults (`primary.appConfig`).
 *   Used as the baseline for `fields` when the override specifies a field set.
 * @returns {object} - New effective appConfig.
 */
export const mergeProductOverride = (base, override, defaults) => {
  if (!override) return base;

  const next = { ...base };

  if (override.fields) {
    next.fields = removeNullFields({
      ...(defaults?.fields ?? {}),
      ...override.fields,
    });
  }

  if (override.localization) {
    next.localization = merge({}, base?.localization, override.localization);
  }

  return next;
};
